/**
 * One error type for everything the API can go wrong with, so callers branch on
 * `status` rather than sniffing message strings.
 *
 * `message` is always safe to show a parent: it comes from the API's error
 * envelope, which is written for humans. `fieldErrors` carries the per-field
 * validation strings when there are any.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, fieldErrors = [], requestId = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.requestId = requestId;
  }

  /** True when retrying could plausibly work: network blips and 5xx. */
  get isRetryable() {
    return this.status === 0 || this.status >= 500;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isRateLimited() {
    return this.status === 429;
  }
}

/**
 * What to put in front of a user. Network and server failures get a line that
 * says what to do next; anything else already has a written message.
 */
export function friendlyMessage(error) {
  if (!(error instanceof ApiError)) {
    return 'Something went wrong. Please try again.';
  }

  if (error.status === 0) {
    return 'We could not reach the server. Check your connection and try again.';
  }

  if (error.isRateLimited) {
    return 'That is a few too many attempts. Please wait a minute and try again.';
  }

  if (error.status >= 500) {
    return 'Something went wrong on our side. Please try again in a moment.';
  }

  return error.message;
}
